import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Plus, Rows3, Table2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { formatDate } from "@/lib/format";
import { PageHeader } from "@/components/portal/PortalLayout";
import {
  Badge, Button, Card, Empty, ErrorNote, Field, Loading, Modal, Note, inputClass,
} from "@/components/portal/Ui";
import { ClearFilters, FilterChip, StickyNote } from "@/components/portal/CampaignBits";
import { CategoryBadge } from "@/components/portal/PlatformBits";
import { CampaignImportModal } from "@/components/portal/CampaignImport";

const STATUSES = ["Upcoming", "Active", "On hold", "Completed", "Archived"];
const CATEGORIES = ["Product Review", "Event Attendance",
                    "Both - Product Review + Event Attendance", "Promotion",
                    "UGC - Content Creation"];
const EVENT_CATEGORIES = ["Event Attendance", "Both - Product Review + Event Attendance"];
const VIEWS = [
  { key: "all", label: "All" },
  { key: "mine", label: "My campaigns" },
  { key: "action", label: "Action needed" },
  { key: "week", label: "Due this week" },
  { key: "archived", label: "Archived" },
];
const VIEW_TEST = (key) => `quick-view-${key}`;

function matchesView(row, view) {
  if (view === "archived") return row.status === "Archived";
  if (row.status === "Archived") return false;
  if (view === "mine") return row.is_mine;
  if (view === "action") return row.needs_action;
  if (view === "week") return row.due_this_week;
  return true;
}

export default function InternalCampaigns() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [creators, setCreators] = useState([]);
  const [staff, setStaff] = useState([]);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [editor, setEditor] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [inviteFor, setInviteFor] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const [layout, setLayout] = useState(() => localStorage.getItem("campaignLayout") || "table");
  const [view, setView] = useState("all");
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const requests = [
        api.get("/campaigns/board"),
        api.get("/client-orgs"),
        api.get("/creators"),
      ];
      if (isAdmin) requests.push(api.get("/users", { params: { role: "internal" } }));
      const [b, o, cr, st] = await Promise.all(requests);
      setRows(b.data);
      setOrgs(o.data);
      setCreators(cr.data);
      if (st) setStaff(st.data);
      setError("");
    } catch (e) {
      setError(apiError(e));
      setRows((prev) => prev || []);
    } finally {
      setRefreshing(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const setLayoutPref = (next) => {
    setLayout(next);
    localStorage.setItem("campaignLayout", next);
  };

  const owners = useMemo(() => {
    const map = new Map();
    (rows || []).forEach((row) => row.owners.forEach((o) => map.set(o.id, o.name)));
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const counts = useMemo(() => {
    const out = {};
    VIEWS.forEach((v) => {
      out[v.key] = (rows || []).filter((row) => matchesView(row, v.key)).length;
    });
    return out;
  }, [rows]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (rows || []).filter((row) => {
      if (!matchesView(row, view)) return false;
      if (clientFilter && row.client_org_id !== clientFilter) return false;
      if (statusFilter && row.status !== statusFilter) return false;
      if (ownerFilter && !row.owners.some((o) => o.id === ownerFilter)) return false;
      if (fromDate && (row.end_date || row.start_date || "") < fromDate) return false;
      if (toDate && (row.start_date || row.end_date || "9999") > toDate) return false;
      if (term) {
        const haystack = [row.name, row.client_org_name, row.sticky_note?.body, row.brief]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [rows, view, search, clientFilter, statusFilter, ownerFilter, fromDate, toDate]);

  const filtersActive = Boolean(
    search || clientFilter || ownerFilter || statusFilter || fromDate || toDate || view !== "all"
  );

  const clearAll = () => {
    setSearch("");
    setClientFilter("");
    setOwnerFilter("");
    setStatusFilter("");
    setFromDate("");
    setToDate("");
    setView("all");
  };

  const openEditor = async (row) => {
    if (!row?.id) return setEditor({});
    try {
      const { data } = await api.get(`/campaigns/${row.id}`);
      setEditor(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openAssignments = async (row) => {
    try {
      const { data } = await api.get(`/campaigns/${row.id}`);
      setAssignFor(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const archive = async (row) => {
    try {
      await api.patch(`/campaigns/${row.id}`, { status: "Archived" });
      toast.success("Campaign archived — history preserved");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleVisibility = async (row) => {
    try {
      const next = row.client_visible === false;
      await api.put(`/campaigns/${row.id}/visibility`, { client_visible: next });
      toast.success(next ? "Campaign is now visible to the client" : "Campaign hidden from the client");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (rows === null) return <Loading label="Loading campaigns…" />;

  const actions = { openEditor, openAssignments, archive, setInviteFor, toggleVisibility, isAdmin, reload: load };

  return (
    <div data-testid="internal-campaigns-page">
      <PageHeader
        title="Campaigns"
        description="Search, filter and keep many campaigns moving. Sticky notes are visible to your whole internal team."
        onRefresh={load}
        refreshing={refreshing}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex overflow-hidden rounded-full border border-[#E4DED3]">
              <button
                type="button"
                onClick={() => setLayoutPref("table")}
                data-testid="layout-table-button"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold transition-colors ${
                  layout === "table" ? "bg-[#111111] text-white" : "bg-white body-text"
                }`}
              >
                <Table2 className="h-3.5 w-3.5" />
                Table
              </button>
              <button
                type="button"
                onClick={() => setLayoutPref("cards")}
                data-testid="layout-cards-button"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold transition-colors ${
                  layout === "cards" ? "bg-[#111111] text-white" : "bg-white body-text"
                }`}
              >
                <Rows3 className="h-3.5 w-3.5" />
                Cards
              </button>
            </div>
            <Button variant="outline" testId="import-campaigns-button" onClick={() => setImportOpen(true)}>
              <Upload className="h-4 w-4" />
              Import CSV
            </Button>
            <Button testId="new-campaign-button" onClick={() => setEditor({})}>
              <Plus className="h-4 w-4" />
              New campaign
            </Button>
          </div>
        }
      />
      <ErrorNote message={error} onRetry={load} />

      <Card className="mb-5" testId="campaign-filters">
        <div className="flex flex-wrap items-center gap-2" data-testid="quick-views">
          {VIEWS.map((v) => (
            <FilterChip
              key={v.key}
              active={view === v.key}
              count={counts[v.key]}
              onClick={() => setView(v.key)}
              testId={VIEW_TEST(v.key)}
            >
              {v.label}
            </FilterChip>
          ))}
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-6">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search campaign, client or note…"
            className={`${inputClass} xl:col-span-2`}
            data-testid="campaign-search-input"
          />
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className={inputClass}
            data-testid="filter-client-select"
          >
            <option value="">All clients</option>
            {orgs.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
          <select
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            className={inputClass}
            data-testid="filter-owner-select"
          >
            <option value="">All owners</option>
            {owners.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={inputClass}
            data-testid="filter-status-select"
          >
            <option value="">All statuses</option>
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2 xl:col-span-2">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className={inputClass}
              aria-label="Running from"
              data-testid="filter-from-date"
            />
            <span className="text-xs body-text">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className={inputClass}
              aria-label="Running to"
              data-testid="filter-to-date"
            />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs body-text" data-testid="campaign-result-count">
            Showing {filtered.length} of {counts[view]} in this view · {rows.length} campaign
            {rows.length === 1 ? "" : "s"} in total
          </p>
          {filtersActive && <ClearFilters onClick={clearAll} testId="clear-filters-button" />}
        </div>
      </Card>

      {rows.length === 0 ? (
        <Empty
          testId="internal-no-campaigns"
          title="No campaigns yet"
          body="Create your first campaign, or import a batch from CSV."
          action={<Button testId="empty-new-campaign-button" onClick={() => setEditor({})}>New campaign</Button>}
        />
      ) : filtered.length === 0 ? (
        <Empty
          testId="no-campaigns-match"
          title="No campaigns match these filters"
          body="Try a different quick view, or clear the filters."
          action={<Button variant="outline" testId="empty-clear-filters" onClick={clearAll}>Clear filters</Button>}
        />
      ) : layout === "table" ? (
        <CampaignTable rows={filtered} actions={actions} />
      ) : (
        <CampaignCards rows={filtered} actions={actions} />
      )}

      <CampaignImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={load}
      />
      <CampaignEditor
        campaign={editor}
        orgs={orgs}
        creators={creators}
        onClose={() => setEditor(null)}
        onSaved={async () => {
          setEditor(null);
          await load();
        }}
      />
      <AssignmentEditor
        campaign={assignFor}
        creators={creators}
        staffUsers={staff}
        isAdmin={isAdmin}
        onClose={() => setAssignFor(null)}
        onSaved={async () => {
          setAssignFor(null);
          await load();
        }}
      />
      <InviteLinkModal campaign={inviteFor} onClose={() => setInviteFor(null)} />
    </div>
  );
}

function ActionPills({ row }) {
  if (!row.needs_action) return <span className="text-xs body-text">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`action-pills-${row.id}`}>
      {row.missing_posts > 0 && (
        <Badge tone="red" testId={`missing-posts-${row.id}`}>{row.missing_posts} posts overdue</Badge>
      )}
      {row.missing_insights > 0 && (
        <Badge tone="orange" testId={`missing-insights-${row.id}`}>{row.missing_insights} insights due</Badge>
      )}
      {row.invoices_pending > 0 && (
        <Badge tone="orange" testId={`invoices-pending-${row.id}`}>{row.invoices_pending} invoices</Badge>
      )}
    </div>
  );
}

function RowActions({ row, actions, stack = false }) {
  const cls = "text-left text-xs font-semibold hover:underline";
  return (
    <div className={stack ? "flex flex-wrap gap-2.5" : "flex flex-wrap items-center gap-x-3 gap-y-1.5"}>
      <button type="button" className={`${cls} text-[#111111]`} onClick={() => actions.openEditor(row)} data-testid={`edit-campaign-button-${row.id}`}>
        Edit
      </button>
      <button type="button" className={`${cls} text-[#111111]`} onClick={() => actions.openAssignments(row)} data-testid={`assign-button-${row.id}`}>
        Assignments
      </button>
      <button type="button" className={`${cls} text-[#F2811D]`} onClick={() => actions.setInviteFor(row)} data-testid={`invite-link-button-${row.id}`}>
        KOL invite link
      </button>
      {actions.isAdmin && (
        <button
          type="button"
          className={`${cls} text-[#2C4D8F]`}
          onClick={() => actions.toggleVisibility(row)}
          data-testid={`toggle-visibility-${row.id}`}
        >
          {row.client_visible === false ? "Show to client" : "Hide from client"}
        </button>
      )}
      {row.status !== "Archived" && (
        <button type="button" className={`${cls} text-[#B3261E]`} onClick={() => actions.archive(row)} data-testid={`archive-button-${row.id}`}>
          Archive
        </button>
      )}
    </div>
  );
}

function CampaignTable({ rows, actions }) {
  return (
    <Card testId="campaign-table-card">
      <div className="scroll-x">
        <table className="w-full min-w-[1120px] text-sm" data-testid="campaign-table">
          <thead>
            <tr className="border-b border-[#EFEAE1] text-left text-xs font-bold uppercase tracking-[0.08em] text-[#8a8378]">
              <th className="pb-2.5 pr-4">Campaign</th>
              <th className="pb-2.5 pr-4">Status</th>
              <th className="pb-2.5 pr-4">Dates</th>
              <th className="pb-2.5 pr-4">Owner</th>
              <th className="pb-2.5 pr-4">Creators</th>
              <th className="pb-2.5 pr-4">Posts</th>
              <th className="pb-2.5 pr-4">Needs action</th>
              <th className="pb-2.5 pr-4">Sticky note</th>
              <th className="pb-2.5 min-w-[150px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-b border-[#F5F1EA] align-top" data-testid={`internal-campaign-${row.id}`}>
                <td className="py-3 pr-4">
                  <p className="font-semibold">{row.name}</p>
                  <p className="text-xs body-text">{row.client_org_name || "—"}</p>
                  {row.client_visible === false && (
                    <p className="mt-0.5 text-[11px] font-semibold text-[#2C4D8F]" data-testid={`hidden-marker-${row.id}`}>
                      Hidden from client
                    </p>
                  )}
                  <div className="mt-1.5">
                    <CategoryBadge category={row.category} testId={`campaign-category-${row.id}`} />
                  </div>
                </td>
                <td className="py-3 pr-4">
                  <Badge testId={`campaign-status-${row.id}`}>{row.status}</Badge>
                </td>
                <td className="py-3 pr-4 whitespace-nowrap body-text">
                  {formatDate(row.start_date)} – {formatDate(row.end_date)}
                  {row.event_date && (
                    <span className="mt-1 block text-xs font-semibold text-[#F2811D]" data-testid={`campaign-event-date-${row.id}`}>
                      Event {formatDate(row.event_date)}
                    </span>
                  )}
                </td>
                <td className="py-3 pr-4">
                  {row.owners.length === 0 ? (
                    <span className="body-text">—</span>
                  ) : (
                    row.owners.map((o) => o.name).join(", ")
                  )}
                </td>
                <td className="py-3 pr-4 tabular">{row.creators_count}</td>
                <td className="py-3 pr-4 tabular whitespace-nowrap">
                  {row.published_posts} / {row.expected_posts}
                </td>
                <td className="py-3 pr-4">
                  <ActionPills row={row} />
                </td>
                <td className="py-3 pr-4">
                  <StickyNote campaign={row} onSaved={actions.reload} compact />
                </td>
                <td className="py-3">
                  <RowActions row={row} actions={actions} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function CampaignCards({ rows, actions }) {
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      {rows.map((row) => (
        <Card key={row.id} testId={`internal-campaign-${row.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#8a8378]">
                {row.client_org_name}
              </p>
              <h2 className="mt-1 text-lg font-extrabold">{row.name}</h2>
              {row.client_visible === false && (
                <p className="text-[11px] font-semibold text-[#2C4D8F]" data-testid={`hidden-marker-card-${row.id}`}>
                  Hidden from client
                </p>
              )}
              <p className="mt-1 text-sm body-text">
                {formatDate(row.start_date)} – {formatDate(row.end_date)}
              </p>
              {row.event_date && (
                <p className="mt-1 text-sm font-semibold text-[#F2811D]" data-testid={`campaign-event-date-${row.id}`}>
                  Event {formatDate(row.event_date)}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <CategoryBadge category={row.category} testId={`campaign-category-${row.id}`} />
              <Badge testId={`campaign-status-${row.id}`}>{row.status}</Badge>
            </div>
          </div>

          <dl className="mt-4 grid grid-cols-3 gap-4">
            <div>
              <dt className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">Creators</dt>
              <dd className="mt-1 text-lg font-extrabold tabular">{row.creators_count}</dd>
            </div>
            <div>
              <dt className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">Posts</dt>
              <dd className="mt-1 text-lg font-extrabold tabular">
                {row.published_posts} / {row.expected_posts}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">Owner</dt>
              <dd className="mt-1 text-sm font-semibold">
                {row.owners.map((o) => o.name).join(", ") || "—"}
              </dd>
            </div>
          </dl>

          <div className="mt-4 border-t border-[#F0ECE4] pt-4">
            <ActionPills row={row} />
          </div>

          <div className="mt-4">
            <StickyNote campaign={row} onSaved={actions.reload} />
          </div>

          <div className="mt-5 border-t border-[#F0ECE4] pt-4">
            <RowActions row={row} actions={actions} stack />
          </div>
        </Card>
      ))}
    </div>
  );
}

function InviteLinkModal({ campaign, onClose }) {
  const [links, setLinks] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(14);

  const load = useCallback(async () => {
    if (!campaign) return;
    try {
      const { data } = await api.get(`/campaigns/${campaign.id}/invites`);
      setLinks(data);
    } catch (e) {
      setError(apiError(e));
    }
  }, [campaign]);

  useEffect(() => {
    setLinks(null);
    setError("");
    load();
  }, [load]);

  const fullUrl = (link) => `${window.location.origin}${link.path}`;

  const create = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/campaigns/${campaign.id}/invites`, { expires_in_days: Number(days) });
      toast.success("Invitation link created");
      await load();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (link) => {
    setError("");
    try {
      await api.post(`/invites/${link.id}/revoke`);
      toast.success("Link revoked");
      await load();
    } catch (e) {
      setError(apiError(e));
    }
  };

  const copy = async (link) => {
    try {
      await navigator.clipboard.writeText(fullUrl(link));
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — select the link and copy it manually");
    }
  };

  return (
    <Modal
      open={Boolean(campaign)}
      onClose={onClose}
      title={`KOL invite link — ${campaign?.name || ""}`}
      testId="invite-link-modal"
    >
      <div className="space-y-4">
        <ErrorNote message={error} testId="invite-link-error" />
        <Note tone="neutral">
          Share this link with KOLs on WhatsApp. Anyone who opens it can register as a creator and
          is added to this campaign automatically. The link never shows campaign details and can
          only ever create a creator account.
        </Note>

        <div className="flex flex-wrap items-end gap-3">
          <Field label="Expires in (days)">
            <input
              type="number"
              min="1"
              max="90"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className={`${inputClass} w-28`}
              data-testid="invite-expiry-input"
            />
          </Field>
          <Button busy={busy} onClick={create} testId="create-invite-button">
            <Plus className="mr-1.5 inline h-3.5 w-3.5" />
            Create link
          </Button>
        </div>

        {links === null ? (
          <Loading />
        ) : links.length === 0 ? (
          <Empty
            title="No invitation links yet"
            body="Create one to start inviting KOLs to this campaign."
            testId="invite-empty"
          />
        ) : (
          <ul className="space-y-3" data-testid="invite-list">
            {links.map((link) => (
              <li
                key={link.id}
                className="rounded-xl border border-[#EDE7DC] p-3.5"
                data-testid={`invite-row-${link.id}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Badge
                    tone={
                      link.state === "Active" ? "green" : link.state === "Expired" ? "warn" : "red"
                    }
                    testId={`invite-state-${link.id}`}
                  >
                    {link.state}
                  </Badge>
                  <span className="text-xs body-text">
                    {link.uses} {link.uses === 1 ? "join" : "joins"} · expires{" "}
                    {formatDate(link.expires_at)} · by {link.created_by_name}
                  </span>
                </div>
                <p
                  className="mt-2.5 break-all rounded-lg bg-[#F7F4EF] px-3 py-2 text-xs"
                  data-testid={`invite-url-${link.id}`}
                >
                  {fullUrl(link)}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => copy(link)} testId={`invite-copy-${link.id}`}>
                    <Copy className="mr-1.5 inline h-3.5 w-3.5" />
                    Copy link
                  </Button>
                  {link.state === "Active" && (
                    <Button
                      variant="danger"
                      onClick={() => revoke(link)}
                      testId={`invite-revoke-${link.id}`}
                    >
                      Revoke
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

function CampaignEditor({ campaign, orgs, creators = [], onClose, onSaved }) {
  const editing = Boolean(campaign?.id);
  const [form, setForm] = useState({});
  const [rows, setRows] = useState([]);
  const [applyToOverrides, setApplyToOverrides] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!campaign) return;
    setForm({
      name: campaign.name || "",
      client_org_id: campaign.client_org_id || "",
      category: campaign.category || "",
      event_date: campaign.event_date || "",
      brief: campaign.brief || "",
      start_date: campaign.start_date || "",
      end_date: campaign.end_date || "",
      status: campaign.status || "Upcoming",
      client_visible: campaign.client_visible !== false,
      posts_per_kol: campaign.posts_per_kol ?? "",
    });
    setApplyToOverrides(false);
    setRows([]);
    setError("");
  }, [campaign]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setRow = (creatorId, key, value) =>
    setRows((list) => list.map((r) => (r.creator_id === creatorId ? { ...r, [key]: value } : r)));


  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const postsPerKol = form.posts_per_kol === "" ? null : Number(form.posts_per_kol);
      if (editing) {
        const { client_visible: _v, posts_per_kol: _p, ...rest } = form;
        await api.patch(`/campaigns/${campaign.id}`, rest);
        if (postsPerKol !== (campaign.posts_per_kol ?? null)) {
          const { data } = await api.put(`/campaigns/${campaign.id}/posts-per-kol`, {
            posts_per_kol: postsPerKol,
            apply_to_overrides: applyToOverrides,
          });
          toast.success(
            `Posts required updated · ${data.kols_updated} KOL${data.kols_updated === 1 ? "" : "s"} updated` +
              (data.kols_kept ? `, ${data.kols_kept} hand-set kept` : "")
          );
        }
      } else {
        await api.post("/campaigns", {
          ...form,
          posts_per_kol: postsPerKol,
          creators: rows.map((r) => ({
            creator_id: r.creator_id,
            expected_posts: r.expected_posts === "" ? null : Number(r.expected_posts),
            posting_deadline: r.posting_deadline || null,
            insight_deadline: r.insight_deadline || null,
          })),
        });
      }
      toast.success(editing ? "Campaign updated" : "Campaign created");
      onSaved();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const unassigned = creators.filter((c) => !rows.some((r) => r.creator_id === c.id));

  return (
    <Modal
      open={Boolean(campaign)}
      onClose={onClose}
      title={editing ? "Edit campaign" : "New campaign"}
      testId="campaign-editor-modal"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="campaign-form">
        <ErrorNote message={error} />
        <Field label="Campaign name" required>
          <input required value={form.name || ""} onChange={set("name")} className={inputClass} data-testid="campaign-name-input" />
        </Field>
        <Field label="Client organisation" required>
          <select
            required
            value={form.client_org_id || ""}
            onChange={set("client_org_id")}
            className={inputClass}
            data-testid="campaign-client-select"
          >
            <option value="">Select a client</option>
            {orgs.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Campaign category" required hint="Shown to the KOL on their assigned campaign.">
          <select
            required
            value={form.category || ""}
            onChange={set("category")}
            className={inputClass}
            data-testid="campaign-category-select"
          >
            <option value="">Select a category</option>
            {CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Brief / instructions">
          <textarea rows={5} value={form.brief || ""} onChange={set("brief")} className={inputClass} data-testid="campaign-brief-input" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Start date">
            <input type="date" value={form.start_date || ""} onChange={set("start_date")} className={inputClass} data-testid="campaign-start-input" />
          </Field>
          <Field label="End date">
            <input type="date" value={form.end_date || ""} onChange={set("end_date")} className={inputClass} data-testid="campaign-end-input" />
          </Field>
        </div>
        {EVENT_CATEGORIES.includes(form.category) && (
          <Field
            label="Event date"
            required
            hint="The day the KOL needs to attend. Campaign start and end dates stay as they are."
          >
            <input
              type="date"
              required
              value={form.event_date || ""}
              onChange={set("event_date")}
              className={inputClass}
              data-testid="campaign-event-date-input"
            />
          </Field>
        )}
        <Field label="Status" required>
          <select value={form.status || "Upcoming"} onChange={set("status")} className={inputClass} data-testid="campaign-status-select">
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Posts required from each KOL"
          hint="The campaign's rule, written down to every KOL — now and whenever one is added. Leave empty for no fixed count."
        >
          <input
            type="number"
            min="0"
            value={form.posts_per_kol ?? ""}
            onChange={set("posts_per_kol")}
            className={inputClass}
            placeholder="e.g. 2"
            data-testid="campaign-posts-per-kol-input"
          />
          {editing && (
            <p className="mt-1.5 text-xs body-text" data-testid="campaign-posts-per-kol-scope">
              Applies to {(campaign?.creators || []).length} KOL
              {(campaign?.creators || []).length === 1 ? "" : "s"} on this campaign.
            </p>
          )}
        </Field>
        {editing
          && String(form.posts_per_kol ?? "") !== String(campaign?.posts_per_kol ?? "")
          && (campaign?.creators || []).some((c) => c.expected_posts_overridden) && (
            <div
              className="rounded-xl border border-[#EFEAE1] bg-[#FBFAF7] p-3.5"
              data-testid="campaign-overrides-choice"
            >
              <p className="text-sm font-semibold">
                {(campaign.creators || []).filter((c) => c.expected_posts_overridden).length} KOL
                {(campaign.creators || []).filter((c) => c.expected_posts_overridden).length === 1
                  ? " has"
                  : "s have"}{" "}
                a hand-set number. What should happen to them?
              </p>
              <div className="mt-2 space-y-1.5 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={!applyToOverrides}
                    onChange={() => setApplyToOverrides(false)}
                    data-testid="campaign-keep-overrides"
                  />
                  Leave hand-set KOLs as they are
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={applyToOverrides}
                    onChange={() => setApplyToOverrides(true)}
                    data-testid="campaign-apply-overrides"
                  />
                  Apply the new number to everyone
                </label>
              </div>
            </div>
          )}
        {!editing && (
          <>
            <label className="flex items-start gap-2.5 rounded-xl border border-[#EFEAE1] p-3.5 text-sm font-semibold">
              <input
                type="checkbox"
                checked={form.client_visible !== false}
                onChange={(e) => setForm((f) => ({ ...f, client_visible: e.target.checked }))}
                className="mt-0.5 h-4 w-4 accent-[#F2811D]"
                data-testid="campaign-client-visible-input"
              />
              <span>
                Visible to the client
                <span className="mt-0.5 block text-xs font-normal body-text">
                  Untick to keep it out of the client&apos;s campaign list while you build it. You
                  can flip this any time from the campaign list.
                </span>
              </span>
            </label>

            <div className="rounded-xl border border-[#EFEAE1] p-3.5" data-testid="campaign-kol-setup">
              <p className="text-sm font-semibold">KOLs on this campaign</p>
              <p className="mt-0.5 text-xs body-text">
                Everyone picked starts on the campaign&apos;s{" "}
                {form.posts_per_kol === "" || form.posts_per_kol == null
                  ? "no fixed count"
                  : `${form.posts_per_kol} posts`}
                . Change a KOL below where the deal differs — the tracker and the invoice readiness
                check read each KOL&apos;s own number.
              </p>

              <div className="mt-3 space-y-2.5">
                {rows.map((row) => (
                  <div
                    key={row.creator_id}
                    className="rounded-xl border border-[#F0ECE4] bg-[#FBFAF7] p-3"
                    data-testid={`campaign-kol-row-${row.creator_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold">
                        {row.creator_name}
                        {String(row.expected_posts ?? "") !== String(form.posts_per_kol ?? "") && (
                          <span
                            className="ml-2 rounded-full bg-[#EDF2FB] px-2 py-0.5 text-[11px] font-bold text-[#2C4D8F]"
                            data-testid={`campaign-kol-overridden-${row.creator_id}`}
                          >
                            Hand-set
                          </span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setRows((list) => list.filter((r) => r.creator_id !== row.creator_id))
                        }
                        data-testid={`campaign-kol-remove-${row.creator_id}`}
                        className="text-xs font-semibold text-[#B3261E] hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="mt-2 grid gap-2.5 sm:grid-cols-3">
                      <Field
                        label="Posts required"
                        hint={
                          String(row.expected_posts ?? "") !== String(form.posts_per_kol ?? "")
                            ? "Hand-set"
                            : undefined
                        }
                      >
                        <input
                          type="number"
                          min="0"
                          value={row.expected_posts}
                          onChange={(e) => setRow(row.creator_id, "expected_posts", e.target.value)}
                          className={inputClass}
                          placeholder="Any"
                          data-testid={`campaign-kol-expected-${row.creator_id}`}
                        />
                      </Field>
                      <Field label="Posting deadline">
                        <input
                          type="date"
                          value={row.posting_deadline}
                          onChange={(e) => setRow(row.creator_id, "posting_deadline", e.target.value)}
                          className={inputClass}
                          data-testid={`campaign-kol-posting-${row.creator_id}`}
                        />
                      </Field>
                      <Field label="Insight deadline">
                        <input
                          type="date"
                          value={row.insight_deadline}
                          onChange={(e) => setRow(row.creator_id, "insight_deadline", e.target.value)}
                          className={inputClass}
                          data-testid={`campaign-kol-insight-${row.creator_id}`}
                        />
                      </Field>
                    </div>
                  </div>
                ))}
                {rows.length === 0 && (
                  <p className="text-sm body-text">
                    No KOLs picked yet — you can also assign them after the campaign exists.
                  </p>
                )}
              </div>

              <select
                value=""
                onChange={(e) => {
                  const picked = creators.find((c) => c.id === e.target.value);
                  if (!picked) return;
                  setRows((list) => [
                    ...list,
                    {
                      creator_id: picked.id,
                      creator_name: picked.display_name,
                      expected_posts: form.posts_per_kol ?? "",
                      posting_deadline: "",
                      insight_deadline: "",
                    },
                  ]);
                }}
                className={`${inputClass} mt-3`}
                data-testid="campaign-kol-add-select"
              >
                <option value="">Add a KOL…</option>
                {unassigned.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.display_name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
        <div className="flex justify-end gap-2.5 pt-1">
          <Button variant="outline" onClick={onClose} testId="campaign-cancel-button">
            Cancel
          </Button>
          <Button type="submit" busy={busy} testId="campaign-save-button">
            {editing ? "Save campaign" : "Create campaign"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function AssignmentEditor({ campaign, creators, staffUsers, isAdmin, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [staffIds, setStaffIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!campaign) return;
    setRows(
      campaign.creators.map((c) => ({
        creator_id: c.creator_id,
        expected_posts: c.expected_posts ?? "",
        posting_deadline: c.posting_deadline || "",
        insight_deadline: c.insight_deadline || "",
      }))
    );
    setStaffIds((campaign.staff || []).map((s) => s.user_id));
    setError("");
  }, [campaign]);

  const available = creators.filter((c) => !rows.some((r) => r.creator_id === c.id));

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.put(`/campaigns/${campaign.id}/creators`, {
        assignments: rows.map((r) => ({
          creator_id: r.creator_id,
          expected_posts: r.expected_posts === "" ? null : Number(r.expected_posts),
          posting_deadline: r.posting_deadline || null,
          insight_deadline: r.insight_deadline || null,
        })),
      });
      if (isAdmin) await api.put(`/campaigns/${campaign.id}/staff`, { user_ids: staffIds });
      toast.success("Assignments saved");
      onSaved();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={Boolean(campaign)}
      onClose={onClose}
      title="Campaign assignments"
      wide
      testId="assignment-modal"
    >
      <form onSubmit={submit} className="space-y-5" data-testid="assignment-form">
        <ErrorNote message={error} />
        <div>
          <h3 className="text-sm font-bold uppercase tracking-[0.1em] text-[#8a8378]">Creators</h3>
          <p className="mt-1.5 text-xs body-text" data-testid="assignment-campaign-default">
            {campaign?.posts_per_kol == null
              ? "This campaign has no fixed post count — set a number per KOL, or set the campaign rule from Edit campaign."
              : `This campaign requires ${campaign.posts_per_kol} post${campaign.posts_per_kol === 1 ? "" : "s"} from each KOL. A KOL added here starts on that number; change it where the deal differs.`}
          </p>
          <div className="mt-3 space-y-3">
            {rows.length === 0 && <p className="text-sm body-text">No creators assigned yet.</p>}
            {rows.map((row, index) => {
              const profile = creators.find((c) => c.id === row.creator_id);
              return (
                <div key={row.creator_id} className="rounded-xl border border-[#EFEAE1] p-4" data-testid={`assignment-row-${row.creator_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-semibold">
                      {profile?.display_name || "Creator"}
                      {String(row.expected_posts ?? "") !== String(campaign?.posts_per_kol ?? "") && (
                        <span
                          className="ml-2 rounded-full bg-[#EDF2FB] px-2 py-0.5 text-[11px] font-bold text-[#2C4D8F]"
                          data-testid={`assignment-overridden-${row.creator_id}`}
                        >
                          Hand-set
                        </span>
                      )}
                    </span>
                    <Button
                      variant="danger"
                      testId={`assignment-remove-${row.creator_id}`}
                      onClick={() => setRows((list) => list.filter((_, i) => i !== index))}
                    >
                      Remove
                    </Button>
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <Field
                      label="Posts required"
                      hint={
                        campaign?.posts_per_kol == null
                          ? "Empty means any number of posts"
                          : `Campaign rule: ${campaign.posts_per_kol}`
                      }
                    >
                      <input
                        type="number"
                        min="0"
                        placeholder="Any"
                        value={row.expected_posts}
                        onChange={(e) =>
                          setRows((list) =>
                            list.map((r, i) => (i === index ? { ...r, expected_posts: e.target.value } : r))
                          )
                        }
                        className={inputClass}
                        data-testid={`assignment-expected-${row.creator_id}`}
                      />
                    </Field>
                    <Field label="Posting deadline">
                      <input
                        type="date"
                        value={row.posting_deadline}
                        onChange={(e) =>
                          setRows((list) =>
                            list.map((r, i) => (i === index ? { ...r, posting_deadline: e.target.value } : r))
                          )
                        }
                        className={inputClass}
                        data-testid={`assignment-posting-deadline-${row.creator_id}`}
                      />
                    </Field>
                    <Field label="Insight deadline">
                      <input
                        type="date"
                        value={row.insight_deadline}
                        onChange={(e) =>
                          setRows((list) =>
                            list.map((r, i) => (i === index ? { ...r, insight_deadline: e.target.value } : r))
                          )
                        }
                        className={inputClass}
                        data-testid={`assignment-insight-deadline-${row.creator_id}`}
                      />
                    </Field>
                  </div>
                </div>
              );
            })}
          </div>
          {available.length > 0 && (
            <div className="mt-3 flex flex-wrap items-end gap-2.5">
              <select
                className={inputClass}
                data-testid="assignment-add-select"
                value=""
                onChange={(e) => {
                  if (!e.target.value) return;
                  setRows((list) => [
                    ...list,
                    {
                      creator_id: e.target.value,
                      expected_posts: campaign?.posts_per_kol ?? "",
                      posting_deadline: "",
                      insight_deadline: "",
                    },
                  ]);
                }}
              >
                <option value="">Add a creator…</option>
                {available.map((creator) => (
                  <option key={creator.id} value={creator.id}>
                    {creator.display_name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {isAdmin && (
          <div>
            <h3 className="text-sm font-bold uppercase tracking-[0.1em] text-[#8a8378]">
              Internal staff on this campaign
            </h3>
            <div className="mt-3 space-y-2">
              {staffUsers.length === 0 && (
                <Note tone="orange">No internal staff accounts exist yet. Invite them from Users & Clients.</Note>
              )}
              {staffUsers.map((member) => (
                <label key={member.id} className="flex items-center gap-2.5 text-sm">
                  <input
                    type="checkbox"
                    checked={staffIds.includes(member.id)}
                    onChange={(e) =>
                      setStaffIds((list) =>
                        e.target.checked ? [...list, member.id] : list.filter((id) => id !== member.id)
                      )
                    }
                    data-testid={`assignment-staff-${member.id}`}
                  />
                  <span className="font-semibold">{member.name}</span>
                  <span className="body-text">{member.email}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2.5">
          <Button variant="outline" onClick={onClose} testId="assignment-cancel-button">
            Cancel
          </Button>
          <Button type="submit" busy={busy} testId="assignment-save-button">
            Save assignments
          </Button>
        </div>
      </form>
    </Modal>
  );
}
